// react
import { ChangeEvent, MouseEvent, useContext, useEffect, useState } from 'react';

// types
import { Option } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextUser } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useCurrencies } from '../../../hooks/useCurrencies';

// mui
import { Box, Button, Grid, InputLabel, SelectChangeEvent, Typography } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';
import { Select } from '../../fields/Select/Select';
import { SelectCurrency } from '../../fields/SelectCurrency/SelectCurrency';

// dialogs
import {
	DialogUpdateProject,
	DialogUpdateProjectProps,
} from '../../dialogs/DialogUpdateProject/DialogUpdateProject';

// theme
import { theme } from '../../../theme';

// utils
import { formatDate, getDaysInMonth } from '../../../utils/dates';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export interface FormEditProjectValues {
	currencyReporting: string;
	currencyLocal: string;
	dateReceiptConfirmation: string;
	fiscalYearStartMonth: string;
	fiscalYearStartDay: string;
	fiscalYearEndMonth: string;
	fiscalYearEndDay: string;
}

// props
export interface FormEditProjectProps {
	hasSubmit?: boolean;
}

export const FormEditProject = ({ hasSubmit = false }: FormEditProjectProps) => {
	// hooks
	const { currencies } = useCurrencies();
	const { t } = useTranslation();

	// context
	const { project, refetchProject } = useContext(ContextProject);
	const { user } = useContext(ContextUser);

	// vars
	const idForm = 'form-edit-project';
	const isWfa = user?.role === 'WFA';

	// defaults
	const optionsMonth: Option[] = Array.from({ length: 12 }, (_, i) => {
		const date = new Date(2024, i, 1);
		const monthName = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date);
		const monthValue = String(i + 1).padStart(2, '0');
		return {
			label: monthName,
			value: monthValue,
		};
	});

	const getOptionsDay = (month: string): Option[] => {
		const monthNumber = Number(month);
		const daysInMonth = getDaysInMonth(monthNumber);

		return Array.from({ length: daysInMonth }, (_, i) => {
			const day = i + 1;
			return {
				label: String(day),
				value: String(day).padStart(2, '0'),
			};
		});
	};

	// state
	const [values, setValues] = useState<FormEditProjectValues>({
		currencyLocal: '',
		currencyReporting: '',
		dateReceiptConfirmation: '',
		fiscalYearStartMonth: optionsMonth[0].value as string,
		fiscalYearStartDay: '01',
		fiscalYearEndMonth: optionsMonth[optionsMonth.length - 1].value as string,
		fiscalYearEndDay: '31',
	});

	// hooks
	const [searchParams, setSearchParams] = useSearchParams();

	// params
	const paramDialog = searchParams.get('dialog');

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;

		setValues((valuesPrev) => {
			const newValues = {
				...valuesPrev,
				[name]: value,
			};

			// Validate and adjust day values when month changes
			if (name === 'fiscalYearStartMonth') {
				const daysInMonth = getDaysInMonth(Number(value));
				const currentDay = Number(valuesPrev.fiscalYearStartDay);

				if (currentDay > daysInMonth) {
					newValues.fiscalYearStartDay = String(daysInMonth).padStart(2, '0');
				}
			}

			if (name === 'fiscalYearEndMonth') {
				const daysInMonth = getDaysInMonth(Number(value));
				const currentDay = Number(valuesPrev.fiscalYearEndDay);

				if (currentDay > daysInMonth) {
					newValues.fiscalYearEndDay = String(daysInMonth).padStart(2, '0');
				}
			}

			return newValues;
		});
	};

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		setSearchParams(searchParams);
	};

	const handleSubmit = (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		searchParams.set('dialog', 'updateProject');
		setSearchParams(searchParams);
	};

	const payload: DialogUpdateProjectProps['payload'] = {};

	if (isWfa) {
		payload.dateConfirmationReceipt = values.dateReceiptConfirmation || null;
	}

	if (featureFlags['LOCAL_CURRENCY'] || featureFlags['IFRS']) {
		if (values.currencyLocal || values.currencyReporting) {
			payload.currencies = [];
		}

		if (featureFlags['LOCAL_CURRENCY'] && values.currencyLocal && payload.currencies) {
			payload.currencies.push({
				currency: values.currencyLocal,
				type: 'LOCAL',
			});
		}

		if (featureFlags['IFRS']) {
			if (values.currencyReporting && payload.currencies) {
				payload.currencies.push({
					currency: values.currencyReporting,
					type: 'REPORTING',
				});
			}

			if (values.fiscalYearStartMonth && values.fiscalYearStartDay) {
				payload.fiscalYearStart = `${values.fiscalYearStartMonth}-${values.fiscalYearStartDay}`;
			}

			if (values.fiscalYearEndMonth && values.fiscalYearEndDay) {
				payload.fiscalYearEnd = `${values.fiscalYearEndMonth}-${values.fiscalYearEndDay}`;
			}
		}
	}

	// parse existing fiscal year values
	useEffect(() => {
		setValues((valuesOld) => {
			const valuesNew = { ...valuesOld };

			if (project?.fiscalYearStart) {
				const [month, day] = project.fiscalYearStart.split('-');
				valuesNew.fiscalYearStartMonth = month;
				valuesNew.fiscalYearStartDay = day;
			}

			if (project?.fiscalYearEnd) {
				const [month, day] = project.fiscalYearEnd.split('-');
				valuesNew.fiscalYearEndMonth = month;
				valuesNew.fiscalYearEndDay = day;
			}

			if (project?.currencies) {
				const currencyReporting = project.currencies.find((c) => c.type === 'REPORTING');
				const currencyLocal = project.currencies.find((c) => c.type === 'LOCAL');

				valuesNew.currencyReporting = currencyReporting?.currency || '';
				valuesNew.currencyLocal = currencyLocal?.currency || '';
			}

			if (project?.dateConfirmationReceipt) {
				valuesNew.dateReceiptConfirmation = formatDate({
					date: new Date(project.dateConfirmationReceipt),
					format: 'YYYY-MM-DD',
				});
			}

			return valuesNew;
		});
	}, [project]);

	return (
		<form id={idForm} onSubmit={handleSubmit}>
			<Box
				sx={{
					borderColor: theme.palette.divider,
					borderRadius: 2,
					borderStyle: 'solid',
					borderWidth: 1,
				}}>
				<Grid container>
					{featureFlags['IFRS'] && (
						<Grid item xs={12}>
							<Box
								sx={{
									borderBottomColor: theme.palette.divider,
									borderBottomStyle: 'solid',
									borderBottomWidth: 1,
									padding: theme.spacing(2),
								}}>
								<Grid alignItems="center" container justifyContent="space-between" spacing={2}>
									<Grid item xs={12} md={6}>
										<InputLabel required={true}>{t('reportingCurrency')}</InputLabel>
										<Typography color="brand.grey.500" variant="caption">
											{t('formUpdateProject.reportingCurrency.description')}
										</Typography>
									</Grid>
									<Grid item xs={12} md={6}>
										<Grid container justifyContent="flex-end" spacing={2}>
											<Grid item xs={12} md={8} lg={4}>
												<SelectCurrency
													aria-label={t('reportingCurrency')}
													currencies={['USD', 'EUR', 'JPY']}
													fullWidth
													hasLabel={false}
													name="currencyReporting"
													onChange={handleChangeValue}
													required={true}
													value={values.currencyReporting}
												/>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Box>
						</Grid>
					)}
					{featureFlags['IFRS'] && featureFlags['LOCAL_CURRENCY'] && (
						<Grid item xs={12}>
							<Box
								sx={{
									borderBottomColor: theme.palette.divider,
									borderBottomStyle: 'solid',
									borderBottomWidth: 1,
									padding: theme.spacing(2),
								}}>
								<Grid alignItems="center" container justifyContent="space-between" spacing={2}>
									<Grid item xs={12} md={6}>
										<InputLabel required={true}>{t('localCurrency')}</InputLabel>
										<Typography color="brand.grey.500" variant="caption">
											{t('formUpdateProject.localCurrency.description')}
										</Typography>
									</Grid>
									<Grid item xs={12} md={6}>
										<Grid container justifyContent="flex-end" spacing={2}>
											<Grid item xs={12} md={8} lg={4}>
												<SelectCurrency
													aria-label={t('localCurrency')}
													currencies={currencies}
													fullWidth
													hasLabel={false}
													name="currencyLocal"
													onChange={handleChangeValue}
													value={values.currencyLocal}
												/>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Box>
						</Grid>
					)}
					{featureFlags['IFRS'] && (
						<Grid item xs={12}>
							<Box
								sx={{
									borderBottomColor: theme.palette.divider,
									borderBottomStyle: 'solid',
									borderBottomWidth: 1,
									padding: theme.spacing(2),
								}}>
								<Grid alignItems="center" container justifyContent="space-between" spacing={2}>
									<Grid item xs={12} md={6}>
										<InputLabel required={true}>{t('fiscalYear')}</InputLabel>
										<Typography color="brand.grey.500" variant="caption">
											{t('formUpdateProject.fiscalYear.description')}
										</Typography>
									</Grid>
									<Grid item xs={12} md={6}>
										<Grid container justifyContent="flex-end" spacing={2}>
											<Grid item xs={12}>
												<Grid container alignItems="flex-end" justifyContent="flex-end" spacing={2}>
													<Grid item xs={6} md={4}>
														<Select
															fullWidth
															label={t('fiscalYearStart')}
															name="fiscalYearStartMonth"
															onChange={handleChangeValue}
															options={optionsMonth}
															required={true}
															value={values.fiscalYearStartMonth}
														/>
													</Grid>
													<Grid item xs={6} md={4}>
														<Select
															aria-label={t('fiscalYearStartDay')}
															fullWidth
															name="fiscalYearStartDay"
															onChange={handleChangeValue}
															options={getOptionsDay(values.fiscalYearStartMonth)}
															required={true}
															value={values.fiscalYearStartDay}
														/>
													</Grid>
												</Grid>
											</Grid>
											<Grid item xs={12}>
												<Grid container alignItems="flex-end" justifyContent="flex-end" spacing={2}>
													<Grid item xs={6} md={4}>
														<Select
															fullWidth
															label={t('fiscalYearEnd')}
															name="fiscalYearEndMonth"
															onChange={handleChangeValue}
															options={optionsMonth}
															required={true}
															value={values.fiscalYearEndMonth}
														/>
													</Grid>
													<Grid item xs={6} md={4}>
														<Select
															aria-label={t('fiscalYearEndDay')}
															fullWidth
															name="fiscalYearEndDay"
															onChange={handleChangeValue}
															options={getOptionsDay(values.fiscalYearEndMonth)}
															required={true}
															value={values.fiscalYearEndDay}
														/>
													</Grid>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Box>
						</Grid>
					)}
					{isWfa && (
						<Grid item xs={12}>
							<Box
								sx={{
									borderBottomColor: theme.palette.divider,
									borderBottomStyle: 'solid',
									borderBottomWidth: 1,
									padding: theme.spacing(2),
								}}>
								<Grid alignItems="center" container justifyContent="space-between" spacing={2}>
									<Grid item xs={12} md={6}>
										<InputLabel>{t('confirmationOfReceipt')}</InputLabel>
										<Typography color="brand.grey.500" variant="caption">
											{t('confirmationOfReceiptDescription')}
										</Typography>
									</Grid>
									<Grid item xs={12} md={6}>
										<Grid alignItems="center" container justifyContent="flex-end" spacing={2}>
											<Grid item xs="auto">
												<Button
													onClick={() => {
														setValues((valuesOld) => {
															return {
																...valuesOld,
																dateReceiptConfirmation: '',
															};
														});
													}}
													variant="outlined">
													{t('reset')}
												</Button>
											</Grid>
											<Grid item xs={true} md={8} lg={4}>
												<Input
													fullWidth
													InputLabelProps={{
														shrink: true,
													}}
													name="dateReceiptConfirmation"
													onChange={handleChangeValue}
													type="date"
													value={values.dateReceiptConfirmation}
												/>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Box>
						</Grid>
					)}
					<Grid item xs={12}>
						<Box
							sx={{
								padding: theme.spacing(2),
							}}>
							<Grid container alignItems="center" justifyContent="space-between" spacing={2}>
								<Grid item xs={12} md="auto">
									<Grid container spacing={1}>
										<Grid item>
											<Typography color="brand.grey.500" variant="caption">
												{t('lastUpdated')}
											</Typography>
										</Grid>
										<Grid item>
											<Typography fontWeight={500} variant="caption">
												{formatDate({
													date: project?.modified || project?.created,
												})}
											</Typography>
										</Grid>
									</Grid>
								</Grid>
								{hasSubmit && (
									<Grid item xs={12} md="auto">
										<Button fullWidth={true} type="submit" variant="contained">
											{t('saveProject')}
										</Button>
									</Grid>
								)}
							</Grid>
						</Box>
					</Grid>
				</Grid>
				<DialogUpdateProject
					isOpen={paramDialog === 'updateProject'}
					onClose={handleCloseDialog}
					onSuccess={() => {
						refetchProject();
					}}
					payload={payload}
				/>
			</Box>
		</form>
	);
};
